import * as React from "react";
import {
  Box,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CircleIcon from "@mui/icons-material/Circle";
import QuizIcon from "@mui/icons-material/Quiz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EmailIcon from "@mui/icons-material/Email";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import trainingbg from "../assets/training.jpg";
import LineBreak from "./LineBreak";

const notCoverItems = [
  "Unit tactics or CQB",
  "Team movement",
  "Mission-specific operational procedures",
];

const focusesOnItems = [
  "Firearm safety and responsible gun handling",
  "Shooting stance and form",
  "Grip mechanics",
  "Sight usage and visual processing",
  "Trigger control",
  "Recoil management",
  "Draw technique",
  "Reload technique",
  "Target-to-target transitions",
  "Muzzle control during movement",
  "Balancing speed and accuracy",
  "Shooting mechanics and decision-making under stress",
  "Self-diagnosis and correction",
  "Understanding Hit Factor scoring and match performance metrics",
];

export default function TrainingEnSection(): React.JSX.Element {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${trainingbg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="training"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary" gutterBottom>
              Training Overview
            </Typography>
            <Typography variant="h6" color="text.primary" gutterBottom>
              <i>
                - Learn the theory. Build the process. Improve independently.
              </i>
            </Typography>
          </Box>

          <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              This training is designed for both new and experienced shooters —
              from people who have recently purchased their first firearm to
              USPSA / IPSC / Steel Challenge competitors. <LineBreak />
              For beginners who are new to firearms, basic firearms training is
              available to build safe, responsible, and correct habits from the
              beginning. For competitive shooters, the training focuses on
              developing the ability to shoot fast, accurately, safely, and
              consistently through a clear understanding of shooting theory and
              live-fire application.
              <LineBreak />I have built my experience in environments that push
              the absolute limits of human shooting performance, including USPSA
              / IPSC and Steel Challenge. I am also an NRA Certified Instructor,
              holding formal credentials in firearm safety management and
              foundational shooting instruction. <LineBreak />
              This training does not focus on tactics. Instead, it is dedicated
              to developing pure shooting fundamentals — the universal elements
              that form the foundation of all shooting, through both theory and
              hands-on application. <LineBreak />
              <b>
                The ultimate goal with my training is not simply to memorize
                techniques, but to understand shooting theory well enough to
                self-diagnose and correct errors independently.{" "}
              </b>
              <LineBreak />
              Training is primarily conducted at Rio Salado Sportsman's Club in
              Mesa, Arizona, which has been my home range for many years.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <PsychologyIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Training Philosophy
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              Competitive shooting is a sport, but it is also one of the few
              shooting environments where
              <b>
                {" "}
                safety, speed, accuracy, repeatability, and decision-making{" "}
              </b>
              are measured objectively and immediately. <LineBreak />
              For this reason, in the United States there has long been a
              natural relationship in which top competitive shooters provide
              shooting instruction to law enforcement, military, and other
              professional users. <LineBreak />
              The reasons are straightforward:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CircleIcon sx={{ mr: 1 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Tactics change depending on the situation"
                  sx={{ color: "text.primary" }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CircleIcon sx={{ mr: 1 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Equipment and mission profiles vary by organization"
                  sx={{ color: "text.primary" }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CircleIcon sx={{ mr: 1 }} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <b>
                      <i>
                        The physical mechanics and principles required to shoot
                        fast and accurately are universal
                      </i>
                    </b>
                  }
                  sx={{ color: "text.primary" }}
                />
              </ListItem>
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              My role is to focus on those universal fundamentals, refine them
              to a high level, and teach the core principles that apply
              regardless of role, mission, or equipment. Good coaching is not
              simply giving students the "right answer." The real value of
              training is teaching students how to think. Like teaching someone
              how to fish instead of simply giving them food, my goal is to give
              shooters the tools to understand, diagnose, and improve their own
              shooting.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <QuizIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Shooting Skill, Not Tactics
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              This training does <b>not</b> cover:
            </Typography>
            <List dense>
              {notCoverItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon
                    sx={{
                      "& .MuiSvgIcon-root": {
                        color: "error.main",
                      },
                    }}
                  >
                    <HighlightOffIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              These elements are inherently dependent on organizational
              doctrine, mission type, and operational environment. <LineBreak />
              Instead, this training focuses on shooting itself:
            </Typography>
            <List dense>
              {focusesOnItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon
                    sx={{
                      "& .MuiSvgIcon-root": {
                        color: "success.main",
                      },
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              These are the skills required by anyone who uses a firearm,
              regardless of profession, discipline, or application.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <QuestionAnswerIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Contact
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              Beginners, competitive shooters, instructors, and professional
              users seeking to improve shooting skill are welcome to inquire.
              Training content is customized based on individual needs and
              objectives. <LineBreak />
              Training is primarily conducted at Rio Salado Sportsman's Club in
              Mesa, Arizona. Other locations may be considered depending on
              class type, range availability, group size, travel requirements,
              and training goals.
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Button
              component="a"
              href="mailto:sharkshooter2011@gmail.com"
              startIcon={<EmailIcon />}
              size="large"
              variant="contained"
              sx={{
                px: 5,
                py: 1.8,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: 999,
                textTransform: "none",
                boxShadow: 3,
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 6,
                },
              }}
            >
              Train With Me!
            </Button>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
